import React, { useContext } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { TFileExtension } from 'types';
import { postMediaFile } from 'backend/additionalInformation';
import { openBrowseFile } from 'utils/openBrowseFile';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';

interface IProps {
	hasFile: boolean;
	accept: TFileExtension[];
	maxFileSizeMB: number;
	testId: string;
	onUpload: ({ fileId, file }: { fileId: string; file: File }) => void;
	onSizeExceeded: () => void;
	onInvalidFormat: () => void;
	onUploadError: () => void;
}

export const FileUploadButton: React.FC<IProps> = ({
	hasFile,
	accept,
	maxFileSizeMB,
	testId,
	onUpload,
	onSizeExceeded,
	onInvalidFormat,
	onUploadError,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const uploadFile = async (files: Nullable<FileList>) => {
		const file = files?.[0];

		if (!file) {
			return;
		}

		const fileExtension = file.name.split('.').pop()?.toLowerCase();

		if (!accept.some((extension) => extension === fileExtension)) {
			onInvalidFormat();
			return;
		}

		if (file.size > 1000 * 1000 * maxFileSizeMB) {
			onSizeExceeded();
			return;
		}

		const formData = new FormData();
		formData.append('file', file, file.name);

		try {
			showLoader();
			const { data } = await postMediaFile({ data: formData });
			onUpload({ fileId: data.fileId, file });
		} catch {
			onUploadError();
		} finally {
			hideLoader();
		}
	};

	const handleClick = () => {
		openBrowseFile({ onChoose: uploadFile, accept: accept.map((extension) => `.${extension}`).join(', ') });
	};

	return (
		<NewButton
			size="small"
			iconLeft={<Icon name={hasFile ? IconName.refresh : IconName.upload} size="lg" />}
			testId={testId}
			onClick={handleClick}>
			<Text
				translationNameSpace="form"
				translationKey={hasFile ? 'replaceButton' : 'uploadButton'}
				asFragment={true}
			/>
		</NewButton>
	);
};
